import isNumber from 'lodash/isNumber';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMUtils from '@spothero/utils/dom';
import DialogBack from 'dialog-back-component';
import DialogClose from 'dialog-close-component';
import DialogTitle from 'dialog-title-component';
import RenderInBody from 'render-in-body-component';

/**
 * Creates a modal dialog.
 */
class Dialog extends Component {
    constructor(props) {
        super(props);

        const {
            width
        } = this.props;

        this._isDynamicWidth = (isNumber(width) || width.indexOf('%') > 0);
        this._isAnimating = false;
        this._childHistory = [];
        this._closeClick = this._closeClick.bind(this);
        this._shimClick = this._shimClick.bind(this);
        this._backClick = this._backClick.bind(this);

        this.state = {
            showBack: false
        };
    }

    componentDidMount() {
        const {
            width
        } = this.props;

        if (this._isDynamicWidth) {
            // percentage width, retain and set
            let newWidth = width;

            if (isNumber(width)) {
                // numerical width, set accordingly
                newWidth = `${width}px`;

                DOMUtils.addClass(this._container, 'Dialog-container-custom');
            }

            this._container.style.width = newWidth;
        }

        setTimeout(() => {
            this._show();
        }, 100);
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            showBack: nextProps.activeContentIndex > 0
        });
    }

    hide(...args) {
        this._isAnimating = true;

        const {
            useShim,
            onHidden
        } = this.props;
        const node = (useShim) ? this._dialog : this._container;
        const showClass = (useShim) ? 'Dialog-showing' : 'Dialog-container-showing';

        DOMUtils.removeClass(node, showClass);
        DOMUtils.removeClass('html', 'Dialog-open');

        window.transitionEnd(node).bind(() => {
            window.transitionEnd(node).unbind();

            if (onHidden) {
                onHidden.apply(null, args);
            }

            this._isAnimating = false;
        });
    }

    _show() {
        this._isAnimating = true;

        const {
            useShim,
            onShown
        } = this.props;
        const node = (useShim) ? this._dialog : this._container;
        const showClass = (useShim) ? 'Dialog-showing' : 'Dialog-container-showing';

        DOMUtils.addClass(node, showClass);
        DOMUtils.addClass('html', 'Dialog-open');

        window.transitionEnd(node).bind(() => {
            window.transitionEnd(node).unbind();

            if (onShown) {
                onShown();
            }

            this._isAnimating = false;
        });
    }

    _closeClick(evt) {
        if (this._isAnimating) { return; }

        const {
            onCloseClick
        } = this.props;

        this.hide();

        if (onCloseClick) {
            onCloseClick();
        }
    }

    _shimClick(evt) {
        if (this.props.closeOnShimClick && DOMUtils.hasClass(evt.target, 'Dialog-wrapper')) {
            this._closeClick();
        }
    }

    _backClick(evt) {
        this.props.onRemoveIndex();
    }

    _renderDialog() {
        const {
            className,
            renderInBody,
            useShim,
            title,
            showClose,
            children,
            width
        } = this.props;
        const classes = classNames(
            'Dialog',
            {'Dialog-inline': !renderInBody},
            className
        );
        const containerClasses = classNames(
            'Dialog-container',
            {[`Dialog-container-${width}`]: !this._isDynamicWidth}
        );

        return (
            <div className={classes} ref={node => { this._dialog = node; }}>
                {useShim && <div className="Dialog-shim" />}
                <div className="Dialog-wrapper" onClick={this._shimClick}>
                    <div className={containerClasses} ref={node => { this._container = node; }}>
                        <div className="Dialog-inner">
                            {title && <DialogTitle title={title} />}
                            <div className="Dialog-ctas">
                                {showClose && <DialogClose onClick={this._closeClick} />}
                                {this.state.showBack && <DialogBack onClick={this._backClick} />}
                            </div>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            renderInBody,
            store
        } = this.props;

        if (renderInBody) {
            return <RenderInBody store={store}>{this._renderDialog()}</RenderInBody>;
        } else {
            return this._renderDialog();
        }
    }
}

Dialog.propTypes = {
    /** The classes to pass. */
    className: PropTypes.string,
    /** Whether to render the modal to the body tag (otherwise renders to current position in dom). */
    renderInBody: PropTypes.bool,
    /** Whether to use the background shim. */
    useShim: PropTypes.bool,
    /** Whether to show the close button. */
    showClose: PropTypes.bool,
    /** Prevents the Dialog from closing on shim click. */
    closeOnShimClick: PropTypes.bool,
    /** The title of the dialog. If one isn't provided then the title component won't render. */
    title: PropTypes.string,
    /** The width of the dialog. (size string, pixel value [integer] or percentage ["100%"]) */
    width: PropTypes.oneOfType([
        PropTypes.oneOf(['extra-slim', 'slim', 'normal', 'wide', 'extra-wide']),
        PropTypes.number
    ]),
    /** A function to execute after the dialog has been shown. */
    onShown: PropTypes.func,
    /** A function to execute after the dialog has been hidden. Use this to manage state in a parent component to remove the dialog from the DOM (see examples). */
    onHidden: PropTypes.func,
    /** A function to execute when the close button or the shim is clicked. */
    onCloseClick: PropTypes.func,
    /** Markup to pass as children to the dialog. Pass either one element or an array of elements. */
    children: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.arrayOf(PropTypes.element)
    ]).isRequired,
    /** Indicates the current index of the content being displayed. Manage this from the DialogContent (see examples) and in the parent component's state. */
    activeContentIndex: PropTypes.number,
    /** A function to execute that helps manage state in a parent component to determine which DialogContent to show. Required when multiple DialogContents are present in the parent. */
    onRemoveIndex: PropTypes.func,
    /** Optional Redux store to pass into the rendered Dialog. Required if the Dialog uses any data or actions from a Redux store. */
    store: PropTypes.object
};

Dialog.defaultProps = {
    renderInBody: true,
    useShim: true,
    showClose: true,
    width: 'normal',
    onShown: null,
    onHidden: null,
    activeContentIndex: 0,
    onSubtractIndex: null,
    closeOnShimClick: true
};

export default Dialog;



// WEBPACK FOOTER //
// ./frontend/src/js/components/dialog/Dialog.jsx